/**
 * The cache writes that keep the reads in this folder current.
 *
 * They live together rather than beside the query each one writes to because a
 * single change fans out across several: a task that changes has to be written
 * to the task itself, to every tasks list that holds it, and — when it carries
 * new events — to that task's events list. Actions call these after a write
 * and the socket hooks call them on a push; the queries call the
 * `addOrUpdate*Cache` ones to seed from a read.
 */
import { QueryClient } from '@tanstack/react-query';
import { Task, TaskEvent } from '@/api';
import isEqual from 'lodash/isEqual';
import {
    FETCH_TASK_EVENTS_QUERY_KEY,
    FETCH_TASK_QUERY_KEY,
    FETCH_TASKS_QUERY_KEY
} from './tasks-query-keys';
// Type-only, so it is erased at build and closes no import cycle with
// `use-fetch-tasks`, which imports the seeding write below.
import type { FetchTasksQuery } from './use-fetch-tasks';

/** Seeds the per-task cache from a read. */
export function addOrUpdateTaskCache(
    queryClient: QueryClient,
    task: Task
): void {
    queryClient.setQueryData<Task>([FETCH_TASK_QUERY_KEY, task.id], () => task);
}

/**
 * The same as `addOrUpdateTaskCache`, plus the list writes that only a real
 * change warrants — use this one when a task has actually changed, and the
 * singular one when simply seeding the cache from a read.
 *
 * Every cached `fetchTasks` list is visited and the task written into the ones
 * whose filters it still matches, so a change shows without a refetch.
 */
export function addOrUpdateAllTasksCache(
    queryClient: QueryClient,
    task: Task
): void {
    addOrUpdateTaskCache(queryClient, task);

    queryClient
        .getQueriesData({
            queryKey: [FETCH_TASKS_QUERY_KEY],
            exact: false
        })
        .forEach((queryData) => {
            const queryKey = queryData[0];
            const fetchTasksQuery = queryKey[1] as FetchTasksQuery;

            if (!fetchTasksQuery) {
                return;
            }

            if (fetchTasksQuery.companyId !== task.companyId) {
                return;
            }

            if (
                fetchTasksQuery.teamIds &&
                fetchTasksQuery.teamIds.length > 0 &&
                !fetchTasksQuery.teamIds.includes(task.team.id)
            ) {
                return;
            }

            if (
                fetchTasksQuery.userIds &&
                fetchTasksQuery.userIds.length > 0 &&
                task.user
            ) {
                if (fetchTasksQuery.useNewUserSystem) {
                    // The query's userIds are companyUserIds, but a pushed
                    // task only carries the user's auth id — the filter can't
                    // be evaluated here, so let the server decide.
                    void queryClient.invalidateQueries({
                        queryKey,
                        exact: true
                    });
                    return;
                }
                if (!fetchTasksQuery.userIds.includes(task.user.id)) {
                    return;
                }
            }

            if (fetchTasksQuery.from) {
                const from = new Date(fetchTasksQuery.from);
                from.setHours(0, 0, 0, 0);
                if (from < new Date(task.startTime)) {
                    return;
                }
            }

            if (fetchTasksQuery.to) {
                const to = new Date(fetchTasksQuery.to);
                to.setHours(23, 59, 59, 999);
                if (to > new Date(task.startTime)) {
                    return;
                }
            }

            if (
                Array.isArray(queryData) &&
                queryData.length >= 2 &&
                queryData[1]
            ) {
                const data = queryData[1] as { pages?: Map<string, Task>[] };

                if (data.pages && Array.isArray(data.pages)) {
                    let doesTaskAlreadyExist = false;
                    let didUpdate = false;
                    data.pages.forEach((page) => {
                        const existingTask = page.get(task.id);
                        if (existingTask) {
                            doesTaskAlreadyExist = true;
                            if (!isEqual(existingTask, task)) {
                                page.set(task.id, task);
                                didUpdate = true;
                            }
                        }
                    });

                    if (!doesTaskAlreadyExist && data.pages.length > 0) {
                        data.pages[0].set(task.id, task);
                        didUpdate = true;
                    }

                    if (didUpdate) {
                        // This is just creating a new pages object to force a re-render
                        const newPages: Map<string, Task>[] = [];
                        data.pages.forEach((page) => {
                            newPages.push(new Map(page));
                        });

                        queryClient.setQueryData(queryData[0], () => ({
                            ...data,
                            pages: newPages
                        }));
                    }
                }
            }
        });
}

/**
 * Drops a task that no longer exists: the task itself, its entry in every
 * cached tasks list, and the events belonging to it.
 */
export function deleteTaskFromCache(
    queryClient: QueryClient,
    taskId: string
): void {
    queryClient.removeQueries({
        queryKey: [FETCH_TASK_QUERY_KEY, taskId]
    });

    queryClient.removeQueries({
        queryKey: [FETCH_TASK_EVENTS_QUERY_KEY, taskId]
    });

    // Removing by id is safe across every cached list — ids are unique, so the
    // lists that never held this task are left as they were.
    queryClient
        .getQueriesData({
            queryKey: [FETCH_TASKS_QUERY_KEY],
            exact: false
        })
        .forEach((queryData) => {
            if (
                Array.isArray(queryData) &&
                queryData.length >= 2 &&
                queryData[1]
            ) {
                const data = queryData[1] as { pages?: Map<string, Task>[] };

                if (data.pages && Array.isArray(data.pages)) {
                    let didUpdate = false;
                    const newPages: Map<string, Task>[] = [];
                    data.pages.forEach((page) => {
                        const newPage = new Map(page);
                        if (newPage.delete(taskId)) {
                            didUpdate = true;
                        }
                        newPages.push(newPage);
                    });

                    if (didUpdate) {
                        queryClient.setQueryData(queryData[0], () => ({
                            ...data,
                            pages: newPages
                        }));
                    }
                }
            }
        });
}

/**
 * Writes events into a task's events list. The list is held as a
 * `Map<string, TaskEvent>`, so an event already there is replaced rather than
 * duplicated; a fresh Map is written back to force the re-render.
 *
 * Nothing is created when the events query has never run — there is no list to
 * add to, and the read will fetch them when it does.
 */
export function addOrUpdateTaskEventsCache(
    queryClient: QueryClient,
    taskId: string,
    events: TaskEvent[]
): void {
    queryClient
        .getQueriesData({
            queryKey: [FETCH_TASK_EVENTS_QUERY_KEY, taskId],
            exact: true
        })
        .forEach((queryData) => {
            if (
                Array.isArray(queryData) &&
                queryData.length >= 2 &&
                queryData[1] instanceof Map
            ) {
                const eventsMap = queryData[1] as Map<string, TaskEvent>;
                events.forEach((event) => {
                    eventsMap.set(event.id, event);
                });
                const newEventsMapToTriggerRerender = new Map(eventsMap);
                queryClient.setQueryData(
                    queryData[0],
                    () => newEventsMapToTriggerRerender
                );
            }
        });
}
